import type { Vehicle } from "@/domain/models/vehicle";
import type { VehicleRepository } from "@/domain/repositories/vehicle.repository";
import { VEHICLES } from "@/data/vehicles";
import { delay } from "@/infrastructure/repositories/mock/delay";

function cloneVehicles(): Vehicle[] {
  return VEHICLES.map((vehicle) => ({
    ...vehicle,
    gallery: [...vehicle.gallery],
    features: [...vehicle.features],
  }));
}

export class MockVehicleRepository implements VehicleRepository {
  private items = cloneVehicles();

  async list(): Promise<Vehicle[]> {
    await delay();
    return this.items.map((vehicle) => ({
      ...vehicle,
      gallery: [...vehicle.gallery],
      features: [...vehicle.features],
    }));
  }

  async getById(id: string): Promise<Vehicle | null> {
    await delay();
    const found = this.items.find((vehicle) => vehicle.id === id);
    if (!found) return null;
    return {
      ...found,
      gallery: [...found.gallery],
      features: [...found.features],
    };
  }

  async create(input: Omit<Vehicle, "id">): Promise<Vehicle> {
    await delay();
    const vehicle: Vehicle = {
      ...input,
      id: `${input.brand}-${input.model}-${input.year}`
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, ""),
      gallery: [...input.gallery],
      features: [...input.features],
    };
    this.items = [vehicle, ...this.items];
    return { ...vehicle, gallery: [...vehicle.gallery], features: [...vehicle.features] };
  }

  async update(id: string, input: Partial<Vehicle>): Promise<Vehicle> {
    await delay();
    const index = this.items.findIndex((vehicle) => vehicle.id === id);
    if (index < 0) throw new Error(`Vehicle not found: ${id}`);

    const current = this.items[index];
    const updated: Vehicle = {
      ...current,
      ...input,
      id,
      gallery: input.gallery ? [...input.gallery] : [...current.gallery],
      features: input.features ? [...input.features] : [...current.features],
    };
    this.items[index] = updated;
    return { ...updated, gallery: [...updated.gallery], features: [...updated.features] };
  }

  async remove(id: string): Promise<void> {
    await delay();
    this.items = this.items.filter((vehicle) => vehicle.id !== id);
  }
}
